import type { ContentConfig } from './types'

export const isVendorLovehoneyOrClue = (vendorHash: string) => ['44c50c3092419d47ef46cc88989ea8', 'a6c7bdcd8d9c99a70441ec2b4c4027'].includes(vendorHash)

export const isVendorLovehoney = (vendorHash: string) => ['f8a4a534e23687130165844051de38', '44c50c3092419d47ef46cc88989ea8'].includes(vendorHash)

export const isPatientInfoNHSContraceptionVendor = (vendorHash: string | string[] | undefined) =>
  (Array.isArray(vendorHash) ? vendorHash[0] : vendorHash) === '95d0f06d7a5f6e3362da6675c9c1c8'

// TODO: three hashes only because dev, staging and production hold separate copies of this product.
// Collapse to one once the environments are synced
export const isMenopauseSubscriptionServiceProduct = (productHash: string | undefined) =>
  productHash === 'dd76623115639ebaeba5b7e0929752fc' || productHash === '6d47c78a81e60d310c494fbf3cd4cc49' || productHash === '1c73e6e6ff86e69181755a0fb38ab173'

export const isNHSContraceptionTreatment = (productHash: string | undefined) => productHash === '63ed36243b2603fa43737764017a66f4'

export const isPFProductExceptContraception = (productHash: string | undefined) =>
  [
    '981e66a9d5e4141d23e9fa357d6946c8', // Sinusitis
    '9bfc9b45feb4d4a25d4db5c4229201d9', // Tonsillitis
    'e58c1ba9be4c3aaf45b05b8f4d763dc9', // Insect Bites
    '97b45311f16821bdca3f544d3516d295', // Impetigo
    'bf70d6eb7ffd5af2a7df822a62e99ef0', // Shingles
    '857156a25cf91b0025438ca2ed44b4e8' // UTI
  ].includes(productHash)

export const isProductED = (categories: string[] | undefined) => !!categories?.includes('Erectile Dysfunction')

export const isProductWL = (categories: string[] | undefined) => !!categories?.includes('Weight Loss')

export const E_SURGERY_HASH = '9dabf1cc4105e0b5575366a2b81d6664'

// Breaking the pattern and using hash over title as the title is subject to change multiple times during QA/dev
// TODO: Rename to match final consultation title once the final title is confirmed and QA/dev is complete
export const isConsultationMenopauseInitiation = (consultationHash: string | string[] | undefined) =>
  (Array.isArray(consultationHash) ? consultationHash[0] : consultationHash) === '9b2ef0e35b290b0a78202843894b7c'

export const isConsultationWeightLoss = (consultationTitle: string | null) => {
  if (!consultationTitle) return false
  const keyword = consultationTitle.toLowerCase()
  return keyword.includes('weight loss') || keyword.includes('weight management')
}

// consultationTitle is passed as null on build time due to no questionnaire
export const isConsultationNHSContraception = (consultationTitle: string | null) => {
  if (!consultationTitle) return false

  const keyword = consultationTitle.toLowerCase()
  return (keyword.includes('contraception') || keyword.includes('contraceptive')) && keyword.includes('nhs')
}

export const contentConfig: ContentConfig = {
  // Patient Page
  'patient.subtitle': ({ consultationTitle }) =>
    isConsultationNHSContraception(consultationTitle)
      ? 'Please enter your personal details.'
      : 'Before you complete a clinical consultation please fill out the patients personal details.',

  // Checkout Shipping
  'checkout.title': ({ vendor, consultationTitle, productHash }) =>
    vendor && ((isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle)) || isNHSContraceptionTreatment(productHash))
      ? 'Request Summary'
      : 'Order Summary',

  'checkout.submitButton': ({ vendor, consultationTitle }) =>
    vendor && isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle) ? 'Confirm details' : 'Go to payment',

  'checkout.shippedTo': ({ vendor, consultationTitle }) =>
    vendor && isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle)
      ? 'If you are deemed eligible for free NHS contraception, where would you like your order shipped to?'
      : 'Where would you like your order shipped to?',

  // Checkout Success - Conditional content based on vendor
  'checkout.success.title': ({ vendor, consultationTitle, productHash }) => {
    if (!vendor) return 'Your order is placed!'

    if (isNHSContraceptionTreatment(productHash)) {
      return 'Request received'
    }

    if (isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle)) {
      return 'Your request has been submitted!'
    }

    return 'Your order is placed!'
  },

  'checkout.success.subtitle': ({ consultationTitle }) =>
    isConsultationNHSContraception(consultationTitle)
      ? "Our pharmacy team will review your request. If there are any questions they will be in touch with you by phone.\n\nIf you haven't had a recent blood pressure reading, we'll send you a link to Shen AI so you can easily take your blood pressure from your phone or computer."
      : null,

  // Checkout Success - Dynamic content
  'checkout.success.orderNumber': ({ orderNumber, productHash }) => {
    if (isNHSContraceptionTreatment(productHash)) {
      return `Your request number is #${orderNumber}`
    }
    return `Your order number is #${orderNumber}`
  },

  'checkout.success.estimatedDelivery': ({ deliveryDate, vendor, consultationTitle }) =>
    vendor && isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle) ? null : `Estimated Delivery is ${deliveryDate}`,

  'checkout.success.emailConfirmation': ({ email, productHash }) => {
    if (isNHSContraceptionTreatment(productHash)) {
      return `Your request has been sent to ${email}. Please be sure to check your junk inbox. Our team will send out order updates as we process your order.\n\nDid you know, by ordering through Evaro, the average person saves themselves 72 hours and saves the NHS £33!`
    }

    return `Your order summary has been sent to ${email}. Please be sure to check your junk inbox. Our team will send out order updates as we process your order.\n\nDid you know, by ordering through Evaro, the average person saves themselves 72 hours and saves the NHS £33!`
  },

  'checkout.success.savings': 'Did you know, by ordering through Evaro, the average person saves themselves 72 hours and saves the NHS £33!',

  'checkout.success.button.return': ({ vendor }) => `Return To ${vendor?.name}`,

  'questionnaire.subtitle': ({ vendor, consultationTitle }) =>
    vendor && isVendorLovehoneyOrClue(vendor.hash) && isConsultationNHSContraception(consultationTitle)
      ? 'in less than 15 minutes!'
      : 'in less than 2 minutes!',

  // Questionnaire Complete - Conditional content based on vendor
  'questionnaire.complete.title': ({ pharmacy, consultationTitle, productHash, noSelectionStep }) => {
    if (pharmacy) {
      return 'Consultation complete! Just a few more steps...'
    }

    // noSelectionStep means nothing is there to pick, so the title can't promise a treatment choice.
    // noSelectionStep should cover isNHSContraceptionTreatment, but some non nhs-public vendors still
    // provide this treatment (like patient.info), once this changes we can collapse further.
    if (isNHSContraceptionTreatment(productHash) || noSelectionStep) {
      return 'You\u2019re nearly there'
    }

    return isConsultationNHSContraception(consultationTitle) ? 'You\u2019re nearly there' : 'You\u2019ve completed the consultation - now select your treatment'
  },

  'NHSContraception.faqs': ({ productHash }) => {
    if (isNHSContraceptionTreatment(productHash)) {
      return [
        {
          question: 'Is it safe & legal?',
          answer:
            'Evaro is regulated by the General Pharmaceutical Council - this is the same regulatory body for any physical pharmacy. Our service is completely legal and operates under strict medical guidelines to ensure your safety and wellbeing.'
        },
        {
          question: 'Are there age restrictions?',
          answer:
            "The service is provided in line with the service specification so you'll need to provide your name, date of birth, and address so we can validate your age against national databases like the electoral roll."
        },
        {
          question: 'Will my GP be made aware of my purchase?',
          answer:
            'Evaro will only inform the GP practice that individuals have accessed the service with their consent. If you agree, Evaro can send a record of the consultation and details of the contraception to your GP surgery. During consultation, we may need to verify some of your health information using your NHS GP health records. This includes checking for previous medical and medication history to reduce risk of any interactions with your hormonal contraception whenever starting a new medication, but it is not required. We are able to inform your GP for you or you can inform them yourself, this will be confirmed in the patient health questionnaire.'
        },
        {
          question: 'How do I know that I am receiving legitimate medication?',
          answer:
            "Evaro only sells legitimate, genuine UK branded medication prescribed to you by a registered prescriber. When buying medication online always look for the green GPhC logo and MHRA logo on the bottom of each page you visit. This ensures that the website is a registered online pharmacy operating legally. You should also look out for the registered pharmacy number, e-Surgery's number is 9012167. Always be aware of counterfeit medication, find out how to tell if medication is legitimate [here](https://www.gov.uk/government/news/know-what-youre-buying)."
        },
        {
          question: 'Is it safe to do the consultations online?',
          answer:
            "Yes. Your safety is our priority. You'll complete a short medical questionnaire, and a GPhC-registered clinician will review your answers. If needed, they'll follow up with follow up questions to ensure everything is safe and appropriate before prescribing. Evaro is a MHRA-licensed provider, which means the service meets strict UK regulations for online prescribing and medication safety."
        },
        {
          question: 'How long does my order take to be delivered?',
          answer:
            'Once your order has been dispatched, the delivery time will depend on the option you selected at checkout. Our free delivery option has a 1-3 working day estimated delivery from the moment your prescription request has been issued. If you selected the guaranteed next day special delivery, your order will be with you by 1:00pm the next working day (as long as the order was placed before 6:00pm on weekdays, 10:30am Saturdays and 7:00am Sundays). If you selected the Royal Mail Tracked 24 option, your order will arrive within 1-3 working days (this may take longer for remote Scottish Highlands and British Isles).'
        },
        {
          question: 'How do I track my order?',
          answer:
            "Once your order is placed, you'll be given a unique tracking number that will be sent to you with your order confirmation by email. You can track your order from your e-Surgery account and also on the Royal Mail website. Keep an eye out for these and check your spam/junk folder if you haven't received anything."
        },
        {
          question: 'Does my delivery require a signature?',
          answer:
            "Where possible, we package medication in letterbox sized parcels, so you won't need to sign for delivery. You will have to sign for Royal Mail guaranteed 1pm next day special delivery."
        },
        {
          question: 'How long does my order take to be sent out?',
          answer:
            "Once your order has been placed it needs to be checked by our prescribers and then issued and packed by our pharmacy team. Occasionally, our prescribers will have further questions or we'll need to verify your age using your ID. This might lead to a delay of your order. Once your medication has been dispensed it will be sent out that same day (if ordered before 6:00pm on weekdays, 10:30am on Saturdays, 7:00am on Sundays. On bank holidays, it will be sent out the next working day)."
        },
        {
          question: 'Is the delivery discreet?',
          answer: 'All orders are delivered in plain packaging by Evaro, with no mention of our company.'
        },
        {
          question: "My order hasn't arrived",
          answer:
            "If you haven't received your package within the expected delivery time, please contact our patient care team using the details below. Email Us: hello@evaro.com Call Us: 01603 931 600"
        },
        {
          question: 'Need help?',
          answer:
            "Our rated 'Excellent' Patient Care team are available to help Monday-Friday 9am-5pm. (Excluding public holidays) Email Us: hello@evaro.com Call Us: 01603 931 600"
        }
      ]
    }
  },

  'questionnaire.welcome.step1Title': ({ consultationHash }) =>
    isConsultationMenopauseInitiation(consultationHash) ? 'Complete your consultation' : 'Answer a few questions',

  'questionnaire.welcome.step2Title': ({ productHash, vendorHash, consultationHash }) =>
    isNHSContraceptionTreatment(productHash)
      ? 'Clinician review'
      : isConsultationMenopauseInitiation(consultationHash)
        ? 'Pay for your subscription'
        : isPatientInfoNHSContraceptionVendor(vendorHash) && isPFProductExceptContraception(productHash)
          ? 'Video consultation with a clinician'
          : 'Choose your treatment',

  'questionnaire.welcome.step1Description': ({ consultationHash }) =>
    isConsultationMenopauseInitiation(consultationHash)
      ? 'Tell us about your experience, symptoms and where you are in your menopause journey'
      : 'Complete a clinically approved consultation about your health and personal details',

  'questionnaire.welcome.step2Description': ({ productHash, vendorHash, consultationHash }) =>
    isNHSContraceptionTreatment(productHash)
      ? 'A clinician will review your consultation and may contact you to book a phone call to discuss the most appropriate treatment option'
      : isConsultationMenopauseInitiation(consultationHash)
        ? 'Dr Charlotte\u2019s team will review your consultation. If you aren\u2019t eligible for treatment you\u2019ll be fully refunded'
        : isPatientInfoNHSContraceptionVendor(vendorHash) && isPFProductExceptContraception(productHash)
          ? 'Evaro or trusted partner pharmacy clinicians review and invite you to an online video consultation'
          : 'Select the best treatment for you and pay. A clinician reviews and approves if appropriate',

  'questionnaire.welcome.step3Description': ({ consultationTitle, consultationHash }) =>
    isConsultationWeightLoss(consultationTitle)
      ? 'Dispatched from our pharmacy in discreet packaging with next day delivery if approved by 6pm'
      : isConsultationMenopauseInitiation(consultationHash)
        ? 'Dispensed from our UK pharmacy and delivered in discreet, recyclable packaging, next-day delivery available'
        : 'Dispatched from our pharmacy in discreet packaging with next day delivery available',

  'questionnaire.welcome.description': ({ productHash, consultationHash }) => {
    if (isConsultationMenopauseInitiation(consultationHash)) {
      return 'Dr Charlotte has designed this consultation so we can find the right treatment path personalised for you'
    }
    if (isNHSContraceptionTreatment(productHash)) {
      return "Whether you're starting contraception for the first time, reordering your pill, or looking to switch, you can do it all online—at a time that suits you"
    }
    return "Tell us a little about your needs and we'll point you in the right direction"
  },

  'questionnaire.complete.subtitle': ({ vendor, consultationTitle, pharmacy, productHash, footer }) => {
    if (isConsultationNHSContraception(consultationTitle)) {
      if (pharmacy) {
        return (
          <>
            <p>
              These medical questions help pharmacists prescribe a contraceptive pill that's safe and appropriate for your needs. The pharmacist will provide
              you with their advice at the pharmacy. To continue, you'll need to:
            </p>
            {footer}
          </>
        )
      }

      return (
        <>
          <p>
            Your answers will be securely sent to one of our pharmacists as a request for oral contraception. They will select the most appropriate type for you
            based on your consultation.
          </p>
          <p className='mt-4'>Our pharmacy team routinely get in touch by phone regarding contraception prescriptions.</p>
        </>
      )
    }

    if (pharmacy) {
      return (
        <>
          <p>
            These medical questions help pharmacists treat you safely in case treatment is recommended. The pharmacist will provide you with their advice at the
            pharmacy. To continue, you'll need to:
          </p>
          {footer}
        </>
      )
    }

    // Temporary patient.info pilot
    if (isPatientInfoNHSContraceptionVendor(vendor?.hash) && isPFProductExceptContraception(productHash)) {
      return (
        <>
          <p>
            A pharmacist will review your answers and invite you to a short video call to talk through your symptoms. Everything happens online, so you won't
            need to go into a pharmacy.
          </p>
          {footer}
        </>
      )
    }

    return (
      <>
        <p>These medical questions help pharmacists treat you safely in case treatment is recommended.</p>
        {footer}
      </>
    )
  },

  // Patient Page
  'patient.title': ({ pharmacy }) => `Who is this ${pharmacy ? 'request' : 'medication'} for?`,

  'patient.description': ({ isLogged, hasPatients }) =>
    !isLogged
      ? "You can log in to select from previously used patient details if you've placed an order before, or enter new patient information as needed."
      : hasPatients
        ? "Select a patient you've previously ordered for or add a new one."
        : "Enter the patient's details to continue."
}
